import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { UltrasonicTestReport } from "../types/ndt";

// Create styles for single-page layout
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 15,
    fontSize: 8,
    lineHeight: 1.2,
    fontFamily: "Helvetica",
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    width: "100%",
    paddingLeft: 20,
  },
  headerBlock: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    width: 80,
    height: 60,
    marginRight: 10,
  },
  headerContent: {
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  swamiLine: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 2,
    textAlign: "center",
  },
  companyName: {
    fontSize: 20,
    fontWeight: "extrabold",
    color: "#000000",
    marginBottom: 2,
  },
  companySubtitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#000000",
    textAlign: "center",
    alignItems: "center",
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 8,
    color: "#000000",
    textAlign: "center",
    alignItems: "center",
    lineHeight: 1.1,
    marginTop: 2,
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#000000",
    padding: 4,
  },
  toReportRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  toBox: {
    width: "50%",
    paddingRight: 4,
    borderRightWidth: 1,
    borderRightColor: "#000000",
    paddingBottom: 4,
  },
  reportBox: {
    width: "50%",
    paddingLeft: 4,
    paddingBottom: 4,
  },
  toLabel: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 2,
  },
  toValue: {
    fontSize: 7,
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
    borderStyle: "dotted",
    paddingBottom: 2,
  },
  subjectClientRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  subjectBox: {
    width: "50%",
    paddingRight: 4,
    borderRightWidth: 1,
    borderRightColor: "#000000",
    paddingBottom: 4,
  },
  clientBox: {
    width: "50%",
    paddingLeft: 4,
    paddingBottom: 4,
  },
  subjectLabel: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 2,
  },
  subjectValue: {
    fontSize: 7,
  },
  jobDetailsTable: {
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 8,
  },
  jobDetailRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
  },
  jobDetailLeft: {
    width: "50%",
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
  },
  jobDetailRight: {
    width: "50%",
    padding: 3,
    fontSize: 6,
  },
  jobDetailLabel: {
    fontWeight: "bold",
    marginRight: 2,
  },
  testParamsTable: {
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 8,
  },
  testParamsHeader: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
  },
  testParamsHeaderCell: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    textAlign: "center",
    fontSize: 6,
    fontWeight: "bold",
  },
  testParamsRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
  },
  testParamsCell: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
  },
  testParamsCellCenter: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
    textAlign: "center",
  },
  detailsRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  detailsLabel: {
    width: "25%",
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
    fontWeight: "bold",
  },
  detailsValue: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
  },
  itemsTable: {
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 8,
  },
  itemsHeader: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
  },
  itemsHeaderCell: {
    padding: 2,
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "center",
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
  },
  srNo: { width: "5%" },
  jobDesc: { width: "20%" },
  heatNo: { width: "15%" },
  quantity: { width: "10%" },
  observation: { width: "30%" },
  remarks: { width: "20%" },
  itemsRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#000000",
  },
  itemsCell: {
    padding: 2,
    fontSize: 5,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
  },
  itemsCellCenter: {
    padding: 2,
    fontSize: 5,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    textAlign: "center",
  },
  remarkRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 8,
  },
  remarkLabel: {
    width: "10%",
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
    fontWeight: "bold",
  },
  remarkValue: {
    flex: 1,
    borderRightWidth: 0.5,
    borderRightColor: "#000000",
    padding: 3,
    fontSize: 6,
  },
  // ✅ UPDATED SIGNATURE STYLES — MORE SPACE
  signatureTable: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000000",
    marginBottom: 8,
  },
  signatureColumn: {
    width: "33.33%",
    padding: 6, // Increased padding
    borderRightWidth: 1,
    borderRightColor: "#000000",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "center",
    minHeight: 80, // Key: more vertical space
  },
  lastSignatureColumn: {
    borderRightWidth: 0,
  },
  signatureTitle: {
    fontSize: 6,
    fontWeight: "bold",
    marginBottom: 4,
    textAlign: "center",
  },
  signatureLine: {
    borderTopWidth: 0.5,
    borderTopColor: "#000000",
    borderStyle: "dashed",
    paddingTop: 8, // More space above line
    marginBottom: 6, // More space below line
    fontSize: 6,
    textAlign: "center",
    minHeight: 28, // Ensures room for name
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  signatureDetails: {
    fontSize: 5,
    textAlign: "center",  
    lineHeight: 1.2,
    marginBottom: 2,
  },
  signatureCompany: {
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 1.2,
  },
  notes: {
    marginTop: 8,
    fontSize: 5,
  },
  notesTitle: {
    fontWeight: "bold",
    marginBottom: 2,
  },
  notesList: {
    paddingLeft: 8,
  },
});

interface UTReportPDFProps {
  report: UltrasonicTestReport;
}

const UTReportPDF: React.FC<UTReportPDFProps> = ({ report }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          <View style={styles.headerBlock}>
            <Image src="/AVIS FINAL LOGO png.png" style={styles.logo} />
            <View style={styles.headerContent}>
              <Text style={styles.swamiLine}>|| Shri Swami Samarth ||</Text>
              <Text style={styles.companyName}>
                AVI'S ENGINEERING & CONSULTANCY
              </Text>
              <Text style={styles.companySubtitle}>NDT SERVICES</Text>
              <View style={styles.companyDetails}>
                <Text>
                  SR No-4/1A; plot on-: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag,
                  Savedi, Ahilyanagar (MS)
                </Text>
                <Text>
                  Ph: 7887985350 | Email: avis.engineering2025@gmail.com
                </Text>
                <Text>GSTIN: 27FLTPM3802F1ZU | PAN: AAMPM4068R</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Title */}
        <View style={styles.title}>
          <Text>ULTRASONIC TEST REPORT</Text>
        </View>

        {/* To / Report No. Row */}
        <View style={styles.toReportRow}>
          <View style={styles.toBox}>
            <Text style={styles.toLabel}>To,</Text>
            <Text style={styles.toValue}>{report.client.name}</Text>
            <Text style={styles.toLabel}>Date of Test :</Text>
            <Text style={styles.toValue}>{report.testDate}</Text>
          </View>
          <View style={styles.reportBox}>
            <Text style={styles.toLabel}>Report No. :</Text>
            <Text style={styles.toValue}>{report.reportNumber}</Text>
            <Text style={styles.toLabel}>Order No. :</Text>
            <Text style={styles.toValue}>{report.orderNumber}</Text>
            <Text style={styles.toLabel}>Order Date :</Text>
            <Text style={styles.toValue}>{report.orderDate}</Text>
          </View>
        </View>

        {/* Subject / Client Row */}
        <View style={styles.subjectClientRow}>
          <View style={styles.subjectBox}>
            <Text style={styles.subjectLabel}>SUBJECT:</Text>
            <Text style={styles.subjectValue}>{report.subject}</Text>
          </View>
          <View style={styles.clientBox}>
            <Text style={styles.subjectLabel}>Client - M/s :</Text>
            <Text style={styles.subjectValue}>{report.client.name}</Text>
          </View>
        </View>

        {/* Job Details Table */}
        <View style={styles.jobDetailsTable}>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Job Description:</Text>
              <Text>{report.jobDescription}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>
                Equipment Specification:
              </Text>
              <Text>{report.equipment.specification}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Equipment:</Text>
              <Text>{report.equipment.name}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Material Grade:</Text>
              <Text>{report.materialGrade}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Serial No:</Text>
              <Text>{report.equipment.serialNumber}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Heat No.:</Text>
              <Text>{report.heatNumber}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Date of Calibration:</Text>
              <Text>{report.equipment.calibrationDate}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Surface Condition:</Text>
              <Text>{report.surfaceCondition}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Method:</Text>
              <Text>{report.method}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>
                Basic Calibration Block:
              </Text>
              <Text>{report.basicCalibrationBlock}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Couplant:</Text>
              <Text>{report.couplant}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>
                Reference Calibration Block:
              </Text>
              <Text>{report.referenceCalibrationBlock}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Reference Gain Level:</Text>
              <Text>{report.referenceGainLevel}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Evaluation DB:</Text>
              <Text>{report.evaluationDb} dB</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Scanning DB:</Text>
              <Text>{report.scanningDb} dB</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Test Range:</Text>
              <Text>{report.testRange}</Text>
            </View>
          </View>
          <View style={styles.jobDetailRow}>
            <View style={styles.jobDetailLeft}>
              <Text style={styles.jobDetailLabel}>Reference Standard:</Text>
              <Text>{report.referenceStandard}</Text>
            </View>
            <View style={styles.jobDetailRight}>
              <Text style={styles.jobDetailLabel}>Acceptance Criteria:</Text>
              <Text>{report.acceptanceCriteria}</Text>
            </View>
          </View>
        </View>

        {/* Test Parameters Table */}
        <View style={styles.testParamsTable}>
          <View style={styles.testParamsHeader}>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Amount of coverage/Scan</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Frequency MHz</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Type of Probe</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Size</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Angle</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>Scanning Overlap</Text>
            </View>
            <View style={[styles.testParamsHeaderCell, { flex: 1 }]}>
              <Text>(dB) SCAN</Text>
            </View>
          </View>
          <View style={styles.testParamsRow}>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.coverage}</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.frequency} MHz</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.probeType}</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.size}</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.angle}°</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>{report.testDetails.scanningOverlap}%</Text>
            </View>
            <View style={[styles.testParamsCellCenter, { flex: 1 }]}>
              <Text>+{report.testDetails.scanDb}dB</Text>
            </View>
          </View>
        </View>

        {/* Details Row */}
        <View style={styles.detailsRow}>
          <View style={styles.detailsLabel}>
            <Text>Details:</Text>
          </View>
          <View style={styles.detailsValue}>
            <Text>{report.jobDescription}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.itemsTable}>
          <View style={styles.itemsHeader}>
            <View style={[styles.itemsHeaderCell, styles.srNo]}>
              <Text>Sr. No.</Text>
            </View>
            <View style={[styles.itemsHeaderCell, styles.jobDesc]}>
              <Text>Job Description</Text>
            </View>
            <View style={[styles.itemsHeaderCell, styles.heatNo]}>
              <Text>Heat No.</Text>
            </View>
            <View style={[styles.itemsHeaderCell, styles.quantity]}>
              <Text>Quantity/KG</Text>
            </View>
            <View style={[styles.itemsHeaderCell, styles.observation]}>
              <Text>Observation</Text>
            </View>
            <View style={[styles.itemsHeaderCell, styles.remarks]}>
              <Text>Remarks</Text>
            </View>
          </View>

          {report.items.map((item, index) => (
            <View key={index} style={styles.itemsRow}>
              <View style={[styles.itemsCellCenter, styles.srNo]}>
                <Text>{item.srNo}</Text>
              </View>
              <View style={[styles.itemsCell, styles.jobDesc]}>
                <Text>{item.jobDescription}</Text>
              </View>
              <View style={[styles.itemsCell, styles.heatNo]}>
                <Text>{item.heatNumber}</Text>
              </View>
              <View style={[styles.itemsCellCenter, styles.quantity]}>
                <Text>{item.quantity}</Text>
              </View>
              <View style={[styles.itemsCell, styles.observation]}>
                <Text>{item.observation}</Text>
              </View>
              <View style={[styles.itemsCell, styles.remarks]}>
                <Text>{item.remarks}</Text>
              </View>
            </View>
          ))}

          {/* Add empty rows to ensure table has at least 4 rows */}
          {Array.from({ length: Math.max(0, 4 - report.items.length) }).map(
            (_, index) => (
              <View key={`empty-${index}`} style={styles.itemsRow}>
                <View
                  style={[styles.itemsCellCenter, styles.srNo, { height: 12 }]}
                >
                  <Text></Text>
                </View>
                <View
                  style={[styles.itemsCell, styles.jobDesc, { height: 12 }]}
                >
                  <Text></Text>
                </View>
                <View style={[styles.itemsCell, styles.heatNo, { height: 12 }]}>
                  <Text></Text>
                </View>
                <View
                  style={[
                    styles.itemsCellCenter,
                    styles.quantity,
                    { height: 12 },
                  ]}
                >
                  <Text></Text>
                </View>
                <View
                  style={[styles.itemsCell, styles.observation, { height: 12 }]}
                >
                  <Text></Text>
                </View>
                <View
                  style={[styles.itemsCell, styles.remarks, { height: 12 }]}
                >
                  <Text></Text>
                </View>
              </View>
            )
          )}
        </View>

        {/* Remark Row */}
        <View style={styles.remarkRow}>
          <View style={styles.remarkLabel}>
            <Text>Remark:</Text>
          </View>
          <View style={styles.remarkValue}>
            <Text>{report.remark}</Text>
          </View>
        </View>

        {/* ✅ UPDATED: Three-Column Signature Table with More Vertical Space */}
               {/* ✅ FINAL: Three-Column Signature Section — Content Snug at Bottom */}
        <View style={styles.signatureTable}>
          {/* Column 1: Name of the Performer */}
          <View style={[styles.signatureColumn]}>
            <Text style={styles.signatureTitle}>Name of the Performer;</Text>
            <View style={styles.signatureLine}>
              <Text>{report.inspector.name}</Text>
            </View>
            {/* Push content to bottom */}
            <View style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'center', textAlign: 'center' }}>
              <Text style={styles.signatureDetails}>
                {report.inspector.certification}
              </Text>
              <Text style={styles.signatureDetails}>
                ({report.inspector.level})
              </Text>
              <Text style={styles.signatureCompany}>
                {report.inspector.company || "AVI'S ENGINEERING & CONSULTANCY"}
              </Text>
            </View>
          </View>

          {/* Column 2: Test Carried Out in presence of */}
          <View style={[styles.signatureColumn]}>
            <Text style={styles.signatureTitle}>
              Test Carried Out in presence of
            </Text>
            <View style={styles.signatureLine}>
              <Text>{report.witness || "—"}</Text>
            </View>
            {/* Push content to bottom */}
            <View style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'center', textAlign: 'center' }}>
              <Text style={styles.signatureDetails}>Client Representative</Text>
              <Text style={styles.signatureCompany}>
                {report.client.name}
              </Text>
            </View>
          </View>

          {/* Column 3: Witnessed by */}
          <View style={[styles.signatureColumn, styles.lastSignatureColumn]}>
            <Text style={styles.signatureTitle}>Witnessed by;</Text>
            <View style={styles.signatureLine}>
              <Text>__________________</Text>
            </View>
            {/* Push content to bottom */}
            <View style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'center', textAlign: 'center' }}>
              <Text style={styles.signatureDetails}>Authorized Signatory</Text>
              <Text style={styles.signatureCompany}>
                {report.client.name}
              </Text>
            </View>
          </View>
        </View>

        {/* Notes */}
        <View style={styles.notes}>
          <Text style={styles.notesTitle}>Notes:</Text>
          <View style={styles.notesList}>
            {report.notes.map((note, index) => (
              <Text key={index} style={{ marginBottom: 1 }}>
                • {note}
              </Text>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default UTReportPDF;